import type { SearchResponseSettings } from "../components/ProductSearchForm";
import type { Product } from "../components/ProductResultTable";

const toSafeNumber = (value: unknown, fallback = 0): number => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
};

export const buildProductDetailAddToCartProps = (product: Product, settings?: SearchResponseSettings) => {
    return {
        product: {id: Number(product.id || 0), name: String(product.name || '')},
        productUrl: String(product.url || ''),
        surfaceId: 'product_add_to_cart',
        selectedWarehouseId: String(settings?.customer_warehouse_id || ''),
        basePrice: toSafeNumber(product.price, 0),
        comparePrice: toSafeNumber(product.compare_price, 0),
        comparePriceSource: String(product.compare_price_source || 'regular_price'),
        unitConversions: Array.isArray(product.unit_conversions) ? product.unit_conversions : [],
        salesPrices: product.pricing || undefined,
        breakpointLookup: product.breakpoint_lookup || undefined,
        defaultCurrencySymbol: String(
            product.currency_symbol
            || settings?.default_currency_symbol
            || settings?.currency_symbol
            || ''
        ).trim(),
        defaultCurrencyCode: String(product.currency_code || settings?.default_currency_code || '').trim(),
        currencyDisplayMode: settings?.currency_display_mode ?? settings?.show_currency_code,
        showCurrencyCode: settings?.show_currency_code === true,
        taxDisplayMode: String(product.tax_display_mode || settings?.tax_display_mode || 'excl'),
        pricesIncludeTax: product.prices_include_tax ?? settings?.prices_include_tax,
        priceDisplayTaxEnabled: product.price_display_tax_enabled ?? settings?.price_display_tax_enabled,
        priceDisplayMultiplier: toSafeNumber(product.price_display_multiplier ?? settings?.price_display_multiplier, 1),
        priceIncludingTaxMultiplier: toSafeNumber(product.price_including_tax_multiplier ?? settings?.price_including_tax_multiplier, 1),
        priceExcludingTaxMultiplier: toSafeNumber(product.price_excluding_tax_multiplier ?? settings?.price_excluding_tax_multiplier, 1),
        priceDisplaySuffix: String(product.price_display_suffix || settings?.price_display_suffix || ''),
        priceDisplaySuffixTemplate: String(product.price_display_suffix_template || settings?.price_display_suffix_template || ''),
        pricingUnavailableMessage: String(product.pricing_unavailable_message || '').trim(),
        quantityBreaksEnabled: product.quantity_breaks_enabled !== false,
        showQuantityBreaksTable: product.show_quantity_breaks_table !== false,
        showPrice: settings?.show_price !== false,
        allowAddToCart: settings?.allow_add_to_cart !== false,
        showStock: settings?.show_stock !== false,
        hideCalculatedPrice: product.hide_calculated_price === true || settings?.hide_calculated_price === true,
        baseUom: String(product.base_uom || 'EA'),
        uomEnabled: product.uom_enabled === true,
        availableUoms: Array.isArray(product.available_uoms) ? product.available_uoms : ['EA'],
        inventoryPolicy: product.inventory_policy || {},
        inventoryPolicyByUom: product.inventory_policy_by_uom || {},
        isPurchasable: product.is_purchasable !== false,
        displayMode: 'full',
        compactLayout: 'controls',
        showQuoteButton: settings?.quote_management_enabled === true,
        canCreateQuotes: settings?.can_create_quotes === true,
        canEditQuotes: settings?.can_edit_quotes === true,
        draftOrdersEnabled: settings?.draft_orders_enabled === true,
        canCreateDraftOrders: settings?.can_create_drafts === true,
        canEditDraftItems: settings?.can_edit_draft_items === true,
        showBackorderLabelOnAddToCart: settings?.show_backorder_label_on_add_to_cart === true,
        backInStockEnabled: settings?.back_in_stock_widget_enabled === true,
        backInStockButtonLabel: String(settings?.back_in_stock_button_label || ''),
        backInStockSuccessMessage: String(settings?.back_in_stock_success_message || ''),
        initialBackInStockTargetKeys: Array.isArray(product.back_in_stock_target_keys) ? product.back_in_stock_target_keys : [],
        serialEnabled: product.serial_enabled === true,
        serialForceBuyAll: product.serial_force_buy_all === true,
        serialDisplayMode: String(product.serial_display_mode || 'dropdown'),
        serialOptionsEndpoint: String(product.serial_options_endpoint || ''),
        isEnquiryProduct: product.is_enquiry_product === true,
        enquiryButtonText: String(product.enquiry_button_text || ''),
        isFavourite: product.is_favourite === true,
        canToggleFavourites: settings?.favourites_can_toggle === true,
        favouritesAjaxUrl: settings?.favourites_ajax_url,
        favouritesNonce: settings?.favourites_nonce,
        favouritesGroupsEnabled: settings?.favourites_groups_enabled === true,
        hideFavouriteButton: true,
        isVariableProduct: product.is_variable_product === true,
        variationAttributes: Array.isArray(product.variation_attributes) ? product.variation_attributes : [],
        availableVariations: Array.isArray(product.available_variations) ? product.available_variations : [],
        defaultVariationAttributes: product.default_variation_attributes || {},
        warehouseSummary: product.warehouse_summary || undefined,
        warehouseSummaryDisplayMode: String(settings?.warehouse_summary_full_display || 'off'),
    };
};
